import { ProfessionalData } from "@/entities/professional";
import { CHECK_PROFESSIONAL_KEY, useModalStore } from "@/shared";
import { useToastStore } from "@/shared/stores";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateProfessionalDataMutation } from "./useUpdateProfessionalDataMutation";
import { useCallback } from "react";
import {
  isCompleteHealthProfessionalData,
  toUpdateProfessionalDataFormData,
} from "../mappers/updateProfessionalData";

const SAVE_SIGNATURE_ERROR_MESSAGE =
  "No se pudo guardar la firma. Intentá nuevamente.";

export const useRequiredSignatureSubmission = (
  professionalData: ProfessionalData,
) => {
  const queryClient = useQueryClient();
  const closeModal = useModalStore((state) => state.closeModal);
  const showToast = useToastStore((state) => state.showToast);
  const updateProfessionalDataMutation = useUpdateProfessionalDataMutation();

  const submitSignature = useCallback(
    async (signature: File) => {
      if (!isCompleteHealthProfessionalData(professionalData)) {
        showToast({
          type: "error",
          title: "No se pudieron validar los datos profesionales.",
          duration: 3000,
        });
        return;
      }

      try {
        const formData = toUpdateProfessionalDataFormData(
          professionalData,
          signature,
        );

        await updateProfessionalDataMutation.mutateAsync(formData);
        await queryClient.invalidateQueries({
          queryKey: [CHECK_PROFESSIONAL_KEY],
        });

        closeModal();
      } catch {
        showToast({
          type: "error",
          title: SAVE_SIGNATURE_ERROR_MESSAGE,
          duration: 3000,
        });
      }
    },
    [
      closeModal,
      professionalData,
      queryClient,
      showToast,
      updateProfessionalDataMutation,
    ],
  );

  return {
    isPending: updateProfessionalDataMutation.isPending,
    submitSignature,
  };
};
